import { useEffect, useRef, useState, useCallback } from 'react'
import { useUser } from './use-user'
import { apiFetch } from '@/utils'
import { useWebSocket } from '@/providers/WebSocketProvider'
import type { Notification, NotificationState } from '@/constants/notifications'

const WS_URL = import.meta.env.VITE_WS_URL

interface UseNotificationsReturn extends NotificationState {
  markAsRead: (notificationId: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  refetch: () => Promise<void>
}

export function useNotifications(): UseNotificationsReturn {
  const { uid, id } = useUser()
  const userId = uid || id
  const { isConnected, addMessageListener } = useWebSocket()
  const fetchNotificationsRef = useRef<() => Promise<void>>(() => Promise.resolve())

  const [state, setState] = useState<NotificationState>({
    notifications: [],
    unreadCount: 0,
    isConnected: false,
    isLoading: true,
    error: null
  })

  // Sync isConnected from unified WS into notification state
  useEffect(() => {
    setState(prev => ({ ...prev, isConnected }))
  }, [isConnected])

  // Fetch notifications from REST API
  const fetchNotifications = useCallback(async () => {
    if (!userId) return

    try {
      const response = await apiFetch('/v1/notifications?limit=50&offset=0', {
        withCredentials: true
      })

      if (!response.ok) throw new Error('Failed to fetch notifications')

      const json = await response.json()
      const rawNotifications = json.data?.notifications ?? json.notifications ?? []
      // Filter out invalid notifications
      const notifications = rawNotifications.filter((n: Notification) => n && n.id && n.createdAt)

      setState(prev => ({
        ...prev,
        notifications,
        unreadCount: notifications.filter((n: Notification) => !n.read).length,
        isLoading: false,
        error: null
      }))
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load notifications'
      }))
    }
  }, [userId])

  // Keep ref updated so message handler always calls current version
  fetchNotificationsRef.current = fetchNotifications

  // Fetch unread count (lightweight polling fallback)
  const fetchUnreadCount = useCallback(async () => {
    if (!userId) return

    try {
      const response = await apiFetch('/v1/notifications/unread-count', {
        withCredentials: true
      })

      if (!response.ok) return

      const json = await response.json()
      const count = json.data?.count ?? json.count

      setState(prev => ({
        ...prev,
        unreadCount: count,
        isLoading: false
      }))
    } catch (error) {
      console.error('Failed to fetch unread count:', error)
    }
  }, [userId])

  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    // Optimistic update
    setState(prev => ({
      ...prev,
      notifications: prev.notifications.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      ),
      unreadCount: Math.max(0, prev.unreadCount - 1)
    }))

    // Always use REST API to persist the change
    try {
      await apiFetch(`/v1/notifications/${notificationId}/read`, {
        method: 'PATCH',
        withCredentials: true
      })
    } catch (error) {
      console.error('Failed to mark as read:', error)
      // Revert optimistic update on failure
      fetchNotifications()
    }
  }, [fetchNotifications])

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    // Optimistic update
    setState(prev => ({
      ...prev,
      notifications: prev.notifications.map(n => ({ ...n, read: true })),
      unreadCount: 0
    }))

    // Always use REST API to persist the change
    try {
      await apiFetch('/v1/notifications/mark-all-read', {
        method: 'POST',
        withCredentials: true
      })
    } catch (error) {
      console.error('Failed to mark all as read:', error)
      fetchNotifications()
    }
  }, [fetchNotifications])

  // Listen for NEW_NOTIFICATION messages from the unified WebSocket
  useEffect(() => {
    if (!userId) return

    const removeListener = addMessageListener((message) => {
      if (message.type === 'NEW_NOTIFICATION') {
        fetchNotificationsRef.current()
      }
    })

    return removeListener
  }, [userId, addMessageListener])

  // Initialize: fetch data and set up polling fallback if no WS
  useEffect(() => {
    if (!userId) {
      setState({
        notifications: [],
        unreadCount: 0,
        isConnected: false,
        isLoading: false,
        error: null
      })
      return
    }

    // Always fetch unread count on load
    fetchUnreadCount()

    // Fallback to polling if no WebSocket URL configured
    if (!WS_URL) {
      console.log('WebSocket URL not configured, using polling')
      const pollInterval = setInterval(fetchUnreadCount, 30000)
      return () => clearInterval(pollInterval)
    }
  }, [userId, fetchUnreadCount])

  return {
    ...state,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications
  }
}
